import { Handshake, Leaf, GraduationCap, Droplets } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const CHALLENGES = [
  {
    no: "01",
    title: "Unstable milk prices",
    body: "Market fluctuations and unpredictable pricing squeeze margins, making a steady, reliable income impossible for dairy-dependent families.",
  },
  {
    no: "02",
    title: "Rising input costs",
    body: "The expense of animal feed, veterinary care and proper shelter keeps climbing — sometimes surpassing the income the herd generates.",
  },
  {
    no: "03",
    title: "Limited knowledge & training",
    body: "Without training in breeding techniques, disease prevention and milk processing, villagers cannot optimise operations or maximise profits.",
  },
];

const PILLARS = [
  {
    icon: Handshake,
    title: "Fair-trade sourcing",
    body: "We buy raw milk direct from village farmers at fair, transparent prices — no exploitative middlemen.",
  },
  {
    icon: Leaf,
    title: "Organic processing",
    body: "Our facilities follow stringent organic standards — no synthetic additives, hormones or chemicals.",
  },
  {
    icon: GraduationCap,
    title: "Clean husbandry training",
    body: "We educate villagers and assist them in establishing clean, hygienic animal-husbandry practices.",
  },
  {
    icon: Droplets,
    title: "Regenerative farming",
    body: "Regenerative techniques, efficient water management and proper shed and food management at village level.",
  },
];

export default function Story() {
  return (
    <section id="story" data-testid="story-section" className="bg-ink text-cream relative">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 lg:py-36">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <Reveal>
                <Eyebrow>01 — The Challenge</Eyebrow>
                <h2 className="mt-6 font-serif font-semibold text-4xl md:text-5xl leading-[1.05] tracking-tight">
                  Why the village dairy farmer stays behind
                </h2>
                <p className="mt-7 text-cream/70 leading-relaxed max-w-md">
                  For families who live off dairy, every season is a gamble. Income swings with the
                  market while costs only rise — and without the right knowledge, hard work alone
                  cannot close the gap. Their livelihood deserves better.
                </p>
              </Reveal>
              <Reveal delay={0.15} className="mt-12">
                <div className="relative overflow-hidden rounded-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1608876537010-ac56d8731614?auto=format&fit=crop&w=1100&q=80"
                    alt="Village dairy farmer with his cattle"
                    className="h-72 md:h-96 w-full object-cover"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-ink/70 backdrop-blur-sm px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-cream/80">
                    Village dairy belt · Vyara, Gujarat
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          <div className="lg:col-span-7">
            {CHALLENGES.map((c, i) => (
              <Reveal key={c.no} delay={i * 0.1}>
                <div
                  data-testid={`story-challenge-${c.no}`}
                  className="group border-t border-cream/15 py-10 px-2 md:px-6 transition-colors duration-500 hover:bg-cream/5"
                >
                  <div className="flex items-start gap-6 md:gap-10">
                    <span className="font-mono text-sm text-gold pt-2">{c.no}</span>
                    <div>
                      <h3 className="font-serif text-2xl md:text-3xl group-hover:text-gold transition-colors duration-500">
                        {c.title}
                      </h3>
                      <p className="mt-3 text-cream/65 leading-relaxed max-w-xl">{c.body}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}

            <div className="border-t border-cream/15" />

            <Reveal delay={0.1} className="mt-20">
              <Eyebrow>02 — The Adveshta Answer</Eyebrow>
              <h2 className="mt-6 font-serif font-semibold text-4xl md:text-5xl leading-[1.05] tracking-tight">
                Fair trade in,
                <br />
                organic purity out.
              </h2>
            </Reveal>

            <div className="mt-12 grid sm:grid-cols-2 gap-5">
              {PILLARS.map((p, i) => (
                <Reveal key={p.title} delay={i * 0.08}>
                  <div
                    data-testid={`story-pillar-${i}`}
                    className="h-full rounded-2xl border border-cream/12 p-7 bg-pine/20 hover:border-gold/50 transition-colors duration-500"
                  >
                    <p.icon className="h-6 w-6 text-gold" />
                    <h3 className="mt-5 font-serif text-xl md:text-2xl">{p.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-cream/60">{p.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.15} className="mt-12">
              <div className="grid md:grid-cols-2 gap-6 items-stretch">
                <div className="relative overflow-hidden rounded-2xl min-h-[240px]">
                  <img
                    src="https://images.unsplash.com/photo-1655325450699-ada3a597d3bc?auto=format&fit=crop&w=1100&q=80"
                    alt="Cattle at the Adveshta shed"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </div>
                <div className="rounded-2xl bg-gold/10 border border-gold/30 p-8 flex flex-col justify-center">
                  <p className="font-serif italic text-xl md:text-2xl leading-snug text-cream/90">
                    “Our own cattle shed of 100+ animals is the classroom — neighbouring villages
                    see the practice, learn it, and make it their own.”
                  </p>
                  <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.28em] text-gold">
                    Adveshta Cattle Shed · Khushalpura
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
